import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { ToastVariants } from '@dotcom/crown-ui';
import { AddressForm } from '../../components/address-form/address-form';
import { AddressFormFooterButtonsProps, GetAddressesProps } from '../../components/address-form/address-form-types';
import { EmailAddressForm } from '../../components/email-address-form/email-address-form';
import { EnvelopePreview } from '../../components/envelope-preview/envelope-preview';
import { Layout } from '../../components/layout';
import { useGetAddresses } from '../../components/recipient-addresses-dialog/hooks/use-get-addresses';
import { SEO } from '../../components/seo/seo';
import { AddressBookProvider } from '../../context/address-book-context/address-book-context';
import { useAnalyticsContext } from '../../context/analytics-context';
import { hideLoadingScreen, setIsToasterOpen, showLoadingScreen, useAppContext } from '../../context/app-context';
import { useCardContext } from '../../context/card-context';
import { useDataContext } from '../../context/data-context/use-data-context';
import { useAddressBookAddresses } from '../../features/address-book/hooks/use-address-book-addresses';
import { AddressTypes, CardType } from '../../global-types';
import { EnvelopeType } from '../../global-types/envelope';
import { useLineItemUUID } from '../../hooks';
import { useIsOneToMany } from '../../hooks/use-is-one-to-many';
import { config, getRegionalConfig } from '../../regional-config';
import { getAccountIdFromCookie, getIsDigitalGreeting, isLoggedIn, loginRedirect } from '../../utils';
import { useLoadExistingProject } from '../editor/hooks/use-load-existing-project';
import styles from './address.module.scss';
import { AddressConfirmation } from './components/address-confirmation/address-confirmation';
import { OnEditAddress } from './components/address-confirmation/address-confirmation-types';
import { AddressFooter } from './components/address-footer/address-footer';
import { useAddressConfirmationDialog } from './hooks/use-address-confirmation-dialog';
import { useAddressEmailSubmit } from './hooks/use-address-email-submit';
import { useAddressForm } from './hooks/use-address-form';
import { useAddressGoBack } from './hooks/use-address-go-back';
import { useAddressSkipValidation } from './hooks/use-address-skip-validation';
import { useEnvelopeAddresses } from './hooks/use-envelope-addresses';
import { useHandleSubmit } from './hooks/use-handle-submit';
import { useLoadCardAfterLogin } from './hooks/use-load-card-after-login';
import { useMigrateAndLoadProject } from './hooks/use-migrate-and-load-project';
import { useOneToManyNavigation } from './hooks/use-one-to-many-navigation';
import { useOneToOneNavigation } from './hooks/use-one-to-one-navigation';
import { populatePreviewImages } from './utils';
import { getIsAddressFormValid } from './utils/get-is-address-form-valid';

export const AddressView = (): ReactElement => {
  // State variables
  const [preloadVerified, setPreloadVerified] = useState(false);
  const [triggerPageRefreshTracking, setTriggerPageRefreshTracking] = useState(true);

  const { appDispatch } = useAppContext();
  const { cardDispatch } = useCardContext();
  const {
    getAddresses,
    isOpen: isDialogOpen,
    setIsOpen: setIsDialogOpen,
    isLoading: isGetAddressesLoading,
  } = useGetAddresses();

  useEffect(() => {
    getAddresses(false);
  }, [getAddresses]);

  const getAddressesProps: GetAddressesProps = useMemo(
    () => ({
      isOpen: isDialogOpen,
      setIsOpen: setIsDialogOpen,
      isLoading: isGetAddressesLoading,
    }),
    [isDialogOpen, isGetAddressesLoading, setIsDialogOpen],
  );

  const lastProjectLoaded = useMemo(() => sessionStorage.getItem('lastProjectLoaded'), []);
  const isDataLoaded = useRef<boolean>(false);

  // Contexts
  const {
    initializedDataState: { data: initializedData, lineItemUUID, isUK, isRecipientAddress },
    setIsRecipientAddress,
  } = useDataContext();
  const { appState } = useAppContext();
  const { previewImages, skipSpellcheckValidation } = appState;
  const isOneToMany = useIsOneToMany();
  const { loadExistingProject } = useLoadExistingProject();
  const { trackRefresh, trackEmailDeliveryRecipient, trackClickAddressRecipient } = useAnalyticsContext();
  useAddressBookAddresses(); // Fetch address book addresses

  // Translation
  const { t } = useTranslation();

  const isDigitalGreeting = getIsDigitalGreeting();
  // Get the addresses to fill based on country and envelope selection
  const addressTypesToFill = useEnvelopeAddresses();
  const { isAddressRegulationToastEnabled } = config;
  const {
    addressForm: { shouldShowOneToManyEnvelopePreview },
  } = getRegionalConfig();

  useEffect(() => {
    if (isAddressRegulationToastEnabled && !isDigitalGreeting) {
      setIsToasterOpen(appDispatch, {
        title: '',
        children: t('addressView.warningShippingMessage'),
        variant: ToastVariants.Warning,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setIsRecipientAddress(false);
  }, [setIsRecipientAddress]);

  // Migrate and load the migrated project after the user logs in and store it in data-context
  useMigrateAndLoadProject();
  // After login personalizationData (used for preview) is empty
  // Populates cardState and personalizationData with the loaded project
  const { loadCardDataAfterLogin } = useLoadCardAfterLogin();
  // Preserve lineItemUUID on query string and data-context
  useLineItemUUID();
  // * Address Confirmation Dialog
  const {
    addAddressToValidate,
    closeAddressConfirmationDialog,
    openAddressConfirmationDialog,
    addressesToValidate,
    isConfirmationDialogOpen,
  } = useAddressConfirmationDialog();

  const { emailValues, handleEmailFormSubmit, isValidEmail, getValuesEmailForm, handleEmailSubmit } =
    useAddressEmailSubmit();

  const loadProject = useCallback(async () => {
    showLoadingScreen(appDispatch, '');
    await loadExistingProject(lastProjectLoaded || '', isDataLoaded);
    hideLoadingScreen(appDispatch);
  }, [appDispatch, lastProjectLoaded, loadExistingProject]);

  const { formValues } = useAddressForm();

  const {
    handleSubmit: handleAddressFormSubmit,
    formState: { isValid: isAddressFormValid, isDirty: isAddressFormTouched },
    getValues,
    watch,
  } = formValues;

  // If card is a 1:many card and the envelope preview is enabled, show the envelope preview
  const shouldShowEnvelope =
    shouldShowOneToManyEnvelopePreview && isOneToMany && initializedData?.project_type_code === CardType.POD;

  const envelope = shouldShowEnvelope ? watch('envelope') : null;

  const { handleAddressSubmit, isAddressLoading, setIsAddressLoading, handleResubmit } = useHandleSubmit({
    openAddressConfirmationDialog,
    closeAddressConfirmationDialog,
    addAddressToValidate,
    envelope,
  });

  const shouldValidateForm = shouldShowEnvelope ? envelope !== EnvelopeType.Blank : true;

  const { onSkipValidation, skipAddressValidation } = useAddressSkipValidation({
    envelope,
    handleResubmit,
  });

  const { goBack } = useAddressGoBack({ isDigitalGreeting, envelope });

  const submitLabel = useMemo(() => {
    // Show preview step when on last addressing step, or when card is DG or One:Many, which have only one step.
    const shouldMoveToPreview =
      (addressTypesToFill.length > 1 && isRecipientAddress) || isDigitalGreeting || isOneToMany;
    return shouldMoveToPreview ? t('addressForm.preview') : t('addressForm.continue');
  }, [addressTypesToFill, isRecipientAddress, t]);

  // Load local project data. Attempts to use data from initialize first
  // falls back to saved session data
  const getLocalProjectData = useCallback(() => {
    if (initializedData !== null) {
      return {
        project_type_code: initializedData.project_type_code,
        account_id: initializedData.account_id,
        project_id: initializedData.project_id,
        lineItemUUID,
      };
    }

    return {
      project_type_code: sessionStorage.getItem('lastProjectTypeCode'),
      account_id: getAccountIdFromCookie(),
      project_id: lastProjectLoaded,
      lineItemUUID,
    };
  }, []);

  useEffect(() => {
    const projectData = getLocalProjectData();
    if (
      projectData.project_type_code === CardType.DG &&
      !isLoggedIn() &&
      !process.env.REACT_APP_IS_ECARD_LOGIN_REDIRECT_DISABLED
    ) {
      // Redirect if we're editing a DG and the user is not logged in
      loginRedirect(projectData.project_id as string, projectData.account_id as string, projectData.lineItemUUID, isUK);
    } else {
      // Show address form if logged in already
      setPreloadVerified(true);
    }
    if (initializedData === null) {
      if (isLoggedIn()) {
        loadCardDataAfterLogin();
      }
      loadProject();
    } else {
      // If initilization data exists, check if we have preview images stored
      // If we don't, user reloaded. Populate state with preview images in assets data
      if (previewImages.length === 0 && initializedData?.assets && initializedData.assets.length > 0) {
        populatePreviewImages(initializedData, appDispatch, cardDispatch);
      }
    }
    if (skipSpellcheckValidation) {
      setIsToasterOpen(appDispatch, {
        title: t('spellcheckWarningToast.title'),
        children: t('spellcheckWarningToast.description'),
        variant: ToastVariants.Warning,
      });
    }
  }, [initializedData]);

  useEffect(() => {
    if (isDataLoaded.current && triggerPageRefreshTracking) {
      trackRefresh();
      setTriggerPageRefreshTracking(false);
      if (isDigitalGreeting) {
        trackEmailDeliveryRecipient();
      } else {
        trackClickAddressRecipient();
      }
    }
  }, [isDataLoaded.current]);

  // When dialog is closed, we want enable the preview button.
  useEffect(() => {
    if (!isConfirmationDialogOpen) {
      setIsAddressLoading(false);
    }
  }, [isConfirmationDialogOpen]);

  const onHandleGoBack = () => {
    const emailFormValues = getValuesEmailForm();
    const addressFormValues = getValues();
    goBack(addressFormValues, emailFormValues);
  };

  const isEmailFormPopulated = Object.values(getValuesEmailForm()).some(
    (value) => value !== '' && value !== null && value !== undefined,
  );

  const isFormValid = getIsAddressFormValid({
    isDigitalGreeting,
    isValidEmail,
    isAddressFormValid,
    shouldValidateForm,
  });

  const handleSubmit = () => {
    // When card is DG or One:Many, which have only one step could fired the submit.
    if (isDigitalGreeting) {
      return handleEmailFormSubmit(handleEmailSubmit);
    }
    return shouldValidateForm ? handleAddressFormSubmit(handleAddressSubmit) : skipAddressValidation;
  };

  const handleOnEditAddress: OnEditAddress = (addressType) => {
    // Set the address step to edit
    setIsRecipientAddress(addressType === AddressTypes.RECIPIENT);
    // Close the confirmation dialog to show the address form
    closeAddressConfirmationDialog();
  };

  const { oneToOneProps } = useOneToOneNavigation({
    submitLabel,
    isFormValid,
    isAddressLoading,
    isAddressFormValid,
    isTouched: isAddressFormTouched,
    handlePrimaryActionButton: handleSubmit(),
    handleSecondaryActionButton: onHandleGoBack,
  });

  const { oneToManyProps, onResubmitAddress } = useOneToManyNavigation({
    isFormValid,
    isAddressLoading,
    isAddressFormValid,
    isGetAddressesLoading,
    handlePrimaryActionButton: handleSubmit(),
    handleSecondaryActionButton: onHandleGoBack,
    addressFormValues: formValues,
    setIsRecipientAddressesDialogOpen: setIsDialogOpen,
    addAddressToValidate,
    openAddressConfirmationDialog,
    closeAddressConfirmationDialog,
  });

  const footerButtonsProps: AddressFormFooterButtonsProps = {
    oneToManyProps,
    oneToOneProps,
  };

  return (
    <AddressBookProvider>
      <Layout hideToolbar toastId="toast">
        <SEO title={`${t('addressView.title')}`} description={`${t('addressView.seo')}`} />
        <div className={styles.address} data-testid="address-view">
          <div className={classNames(styles.content)}>
            {shouldShowEnvelope && <EnvelopePreview selectedEnvelope={envelope as EnvelopeType} />}
            {isDigitalGreeting ? (
              <EmailAddressForm formHandlers={emailValues} isEmailFormPopulated={isEmailFormPopulated} />
            ) : (
              preloadVerified && (
                <AddressForm
                  formHandlers={formValues}
                  onSkipValidation={onSkipValidation}
                  getAddressesProps={getAddressesProps}
                  footerButtonsProps={footerButtonsProps}
                />
              )
            )}
          </div>
          <AddressFooter
            isValid={isFormValid}
            isAddressLoading={isAddressLoading}
            goBack={onHandleGoBack}
            submitLabel={submitLabel}
            handleSubmit={handleSubmit()}
          />
        </div>
        {isConfirmationDialogOpen && (
          // 1:1 address validation
          <AddressConfirmation
            isOpen={isConfirmationDialogOpen}
            onClose={closeAddressConfirmationDialog}
            addressesToValidate={addressesToValidate}
            onSkipValidation={onSkipValidation}
            onEditAddress={handleOnEditAddress}
            isAddressFromAddressBook={false}
            onResubmitAddress={onResubmitAddress}
          />
        )}
      </Layout>
    </AddressBookProvider>
  );
};
